import * as React from "react";
import {ViewColumn} from "../../../model/workspace/ViewColumn";
import {ColumnTypeEnum} from "../../../model/workspace/ColumnDefinition";
import {parseIntoTimeFrame} from "./presenter/TimePeriodPresenter";
import {isDefined} from "../../../utils/Utils";
import "./workspaceCardList.scss";

interface Props {
	/**
	 * The view's columns; the card picks what it shows from them by their type.
	 */
	columns: ViewColumn[];
	data: any[];
	rowToId: (row: any) => string;
	onRowClick?: (rowId: string, rowData: any) => void;
	isLoading?: boolean;
	noDataText?: string;
	/**
	 * Renders a value the way the table does, for the values shown as they are there (a creator, an amount).
	 */
	renderValue: (column: ViewColumn, value: any, row: any) => React.ReactNode;
}

const TITLE_PATHS = ["referenceNumber", "name"];
const STATUS_TYPES = [ColumnTypeEnum.JobStatus, ColumnTypeEnum.LeadStatus, ColumnTypeEnum.BillStatus];
/**
 * The money a job carries; both shown when the row has them.
 */
const AMOUNT_PATHS = ["creditValue", "creditAmount"];
/**
 * How to reach the row's company, a line each: what is needed first when a phone is all there is at hand.
 */
const CONTACT_PATHS = ["phoneNumber", "email"];
/**
 * How many columns a card shows, as name and value, for a view with none of the columns it knows to summarise.
 */
const FALLBACK_FIELDS = 4;

/**
 * A workspace's rows on a phone: a card per row with what tells rows apart - its reference or name, how recent it
 * is, its type and status, its partner brand and who created it, and its credit - rather than every column of the
 * view, one under another, which no one reads on a phone.
 */
export default class WorkspaceCardList extends React.PureComponent<Props> {
	render() {
		if (this.props.isLoading && !this.props.data.length) {
			return null;
		}
		if (!this.props.data.length) {
			return <div className={"c-workspace-cards_empty"}>{this.props.noDataText || "No results"}</div>;
		}
		const visible = this.props.columns.filter(c => c.visible);
		// The summary does not depend on which columns the view shows: a view without a job type column still
		// has the job type in its rows, and a card without it misses what most tells jobs apart.
		const all = this.props.columns;
		const ofType = (type: ColumnTypeEnum) => all.filter(c => c.columnDefinition.type === type)[0];
		const title = all.filter(c => TITLE_PATHS.indexOf(c.columnDefinition.path) >= 0)[0] || visible[0];
		const ofPath = (path: string) => all.filter(c => c.columnDefinition.path === path)[0];
		const status = STATUS_TYPES.map(ofType).filter(isDefined)[0];
		// How recently a row moved, for rows that move through statuses; a partner brand's date added says nothing
		// worth a line.
		const dates = status ? all.filter(c => c.columnDefinition.type === ColumnTypeEnum.Date) : [];
		const date = dates.filter(c => /update|action/i.test(c.columnDefinition.path))[0] || dates[0];
		// What kind of row it is, over its title: a job's type. Not a partner brand's parent brand: production has
		// a single Brand, so it would say the same on every card.
		const kind = ofType(ColumnTypeEnum.JobType);
		const partnerBrand = ofType(ColumnTypeEnum.PartnerBrand);
		const contacts = CONTACT_PATHS.map(ofPath).filter(isDefined);
		const leadUser = ofPath("leadUserId");
		// Who created a row matters beside the partner it is for, as on a job; a partner brand's card stays at its
		// name, how to reach it and how long it has been with us.
		const creator = partnerBrand ? ofType(ColumnTypeEnum.Creator) : undefined;
		const amounts = AMOUNT_PATHS.map(ofPath).filter(isDefined);
		const knowsTheRow = !!status || !!kind || !!partnerBrand || contacts.length > 0;
		const summarised = [title, date, status, kind, partnerBrand, ...contacts, leadUser, creator, ...amounts];
		const fallback = knowsTheRow ? [] : visible.filter(c => summarised.indexOf(c) < 0).slice(0, FALLBACK_FIELDS);

		return (
			<ul className={"c-workspace-cards"}>
				{this.props.data.map(row => {
					const id = this.props.rowToId(row);
					const statusText = status ? textOf(valueOf(row, status)) : "";
					const kindText = kind ? textOf(valueOf(row, kind)) : "";
					const titleText = title ? textOf(valueOf(row, title)) : "";
					const whenText = date && isDefined(valueOf(row, date)) ? parseIntoTimeFrame(valueOf(row, date)) : "";
					// Without a time or a status the right column would sit empty and halve the width of the rest.
					const hasAside = !!whenText || !!statusText;
					const shownAmounts = amounts.filter(c => isDefined(valueOf(row, c)));
					return (
						<li key={id}>
							{/* Laid out as agreed on the mock-up (card C): what the row is on the left, where it stands on
							the right, as a mail app puts the time beside the sender, and the money underneath. */}
							<button type={"button"}
									className={`c-workspace-cards_card${hasAside ? "" : " is-single-column"}`}
									onClick={() => this.props.onRowClick && this.props.onRowClick(id, row)}>
								<span className={"c-workspace-cards_main"}>
									{kindText && <span className={"c-workspace-cards_type"}>{kindText}</span>}
									<span className={"c-workspace-cards_title"}>{titleText}</span>
									{partnerBrand && textOf(valueOf(row, partnerBrand)) &&
									<span className={"c-workspace-cards_line is-brand"}>{textOf(valueOf(row, partnerBrand))}</span>}
									{contacts.map((c, i) => textOf(valueOf(row, c)) &&
										<span key={c.id} className={`c-workspace-cards_line${i === 0 ? " is-brand" : ""}`}>
											{textOf(valueOf(row, c))}
										</span>)}
									{leadUser && isDefined(valueOf(row, leadUser)) &&
									<span className={"c-workspace-cards_line is-secondary is-labelled"}>
										<span className={"c-workspace-cards_label"}>LU:</span>
										{this.props.renderValue(leadUser, valueOf(row, leadUser), row)}
									</span>}
									{creator && isDefined(valueOf(row, creator)) &&
									<span className={"c-workspace-cards_line is-secondary"}>
										{this.props.renderValue(creator, valueOf(row, creator), row)}
									</span>}
									{fallback.map(c =>
										<span key={c.id} className={"c-workspace-cards_field"}>
											<span className={"c-workspace-cards_label"}>{c.columnDefinition.label}</span>
											<span className={"c-workspace-cards_value"}>{this.props.renderValue(c, valueOf(row, c), row)}</span>
										</span>)}
								</span>
								{hasAside &&
								<span className={"c-workspace-cards_aside"}>
									{whenText && <span className={"c-workspace-cards_when"}>{whenText}</span>}
									{statusText && <span className={"c-workspace-cards_status"}>{statusText}</span>}
								</span>}
								{shownAmounts.length > 0 &&
								<span className={"c-workspace-cards_amounts"}>
									{shownAmounts.map(c =>
										<span key={c.id} className={"c-workspace-cards_amount"}>
											<span className={"c-workspace-cards_label"}>{c.columnDefinition.label}</span>
											{this.props.renderValue(c, valueOf(row, c), row)}
										</span>)}
								</span>}
							</button>
						</li>
					);
				})}
			</ul>
		);
	}
}

/**
 * The column's value in the row, following a dotted path the way the table's accessor does.
 */
function valueOf(row: any, column: ViewColumn): any {
	return column.columnDefinition.path.split(".").reduce((value, key) => isDefined(value) ? value[key] : undefined, row);
}

/**
 * A value as words: a string as it is, a brand by its name.
 */
function textOf(value: any): string {
	if (!isDefined(value)) {
		return "";
	}
	if (typeof value === "string" || typeof value === "number") {
		return String(value);
	}
	if (isDefined(value.name)) {
		return value.archived ? `${value.name} (archived)` : String(value.name);
	}
	return "";
}
